import { Dispatch, RefObject, SetStateAction, useEffect, useRef, useState } from "react";

import { composedPathContains } from "@/utils/getComposedEventTarget";

export type PopoverType = "status" | "enrich" | "archive" | "snooze" | "blacklist" | "addTo" | null;

interface TableFooterPopovers {
	openPopover: PopoverType;
	setOpenPopover: Dispatch<SetStateAction<PopoverType>>;
	togglePopover: (type: PopoverType) => void;
	showListPopover: boolean;
	setShowListPopover: Dispatch<SetStateAction<boolean>>;
	popoverRef: RefObject<HTMLDivElement>;
}

/**
 * Owns the bulk-action-footer popover state: which group popover is open, the nested list popover,
 * and the close-on-outside-click listener anchored to the popover container. Extracted from
 * {@link TableFooter} to keep that file under the size cap.
 */
export const useTableFooterPopovers = (): TableFooterPopovers => {
	const [openPopover, setOpenPopover] = useState<PopoverType>(null);
	const [showListPopover, setShowListPopover] = useState(false);
	const popoverRef = useRef<HTMLDivElement>(null);

	const togglePopover = (type: PopoverType) => {
		setOpenPopover((prev) => (prev === type ? null : type));
	};

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (popoverRef.current && !composedPathContains(popoverRef.current, e)) {
				setOpenPopover(null);
				setShowListPopover(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	return {
		openPopover,
		setOpenPopover,
		togglePopover,
		showListPopover,
		setShowListPopover,
		popoverRef,
	};
};
